import DashboardService from "../../../services/dashboard.service";

// Switching back to a period loaded in the last minutes is instant. Same lifetime as the backend cache
const MAX_AGE_MS = 5 * 60 * 1000;

const responses = new Map();
const loading = new Map();

const formatDay = (day) => day.format("YYYY-MM-DD");

// Parts of the DataLayer Guard response the homepage and the quick actions use
const DATALAYER_GUARD_PARTS = ["errors", "errors_timeline", "total_events_timeline"];

/**
 * Request of one Tag Monitor chart type for a period
 *
 * @param {object} options - companyId, chartType, period (see getPeriod) and granularity ("summary" on the homepage,
 *   null for the full rows of the dashboard)
 * @returns {{key: string, load: function}} Key of the request and the function that loads its response context
 */
export const tagMonitorRequest = ({ companyId, chartType, period, granularity = "summary" }) => ({
  key: [companyId, chartType, granularity, formatDay(period.start), formatDay(period.end), period.window].join("|"),
  load: async () => {
    const response = await DashboardService.getTagMonitorDashboard(
      {
        companyId,
        startDate: formatDay(period.start),
        endDate: formatDay(period.end),
        differenceOneDayOrOneHour: period.window,
        filtersType: "client",
        granularity,
      },
      chartType,
    );
    if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

    const respData = await response.json();
    // A failed part is missing data, not empty data; failing the request also keeps it out of the cache
    const failedParts = respData?.context?.failed_parts || [];
    if (failedParts.length) throw new Error(`Tag Monitor ${failedParts.join(", ")} could not be loaded`);
    return respData?.context || {};
  },
});

/**
 * Request of the DataLayer Guard dashboard data for a period
 *
 * @param {object} options - companyId and period (see getPeriod)
 * @returns {{key: string, load: function}} Key of the request and the function that loads its response context
 */
export const dataLayerGuardRequest = ({ companyId, period }) => ({
  key: [companyId, "dlg", formatDay(period.start), formatDay(period.end), period.window].join("|"),
  load: async () => {
    const response = await DashboardService.getDLGDashboardData({
      companyId,
      start_date: period.start.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
      end_date: period.end.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
      differenceOneDayOrOneHour: period.window,
    });
    if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

    // Without a dataset the company has no DataLayer Guard data yet, which is not an error
    const respData = await response.json();
    // The homepage needs these parts, without one of them the error rate would look like 0%
    const failedParts = (respData?.context?.failed_parts || []).filter((part) => DATALAYER_GUARD_PARTS.includes(part));
    if (failedParts.length) throw new Error(`DataLayer Guard ${failedParts.join(", ")} could not be loaded`);
    return respData?.dataset_exists ? respData.context || {} : {};
  },
});

/**
 * Cached response context of a request
 *
 * @param {string} key - Key of the request
 * @returns {object|undefined} Response context, undefined when it is not loaded or too old
 */
export const getCachedResponse = (key) => {
  const cached = responses.get(key);
  if (!cached) return undefined;
  if (Date.now() - cached.time < MAX_AGE_MS) return cached.context;

  responses.delete(key);
  return undefined;
};

/**
 * Loads a request once: a cached response is returned at once and a request that is already loading (e.g. by
 * another part of the page) is shared instead of sent again. A failed request is not cached, so it is tried again
 *
 * @param {{key: string, load: function}} request - Request, see tagMonitorRequest and dataLayerGuardRequest
 * @returns {Promise<object>} Response context, rejects when the request failed
 */
export const loadRequest = ({ key, load }) => {
  const cached = getCachedResponse(key);
  if (cached !== undefined) return Promise.resolve(cached);

  if (!loading.has(key)) {
    const promise = load()
      .then((context) => {
        responses.set(key, { context, time: Date.now() });
        return context;
      })
      .finally(() => loading.delete(key));
    loading.set(key, promise);
  }
  return loading.get(key);
};
